import axios from 'axios';
import { store } from '../store/store';
import { logout } from '../store/authSlice';
import OfflineStorage from './OfflineStorage';

// Usa sempre URL relativo - il proxy o il server gestirà il routing
const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Offline: nessuna risposta dal server (rete assente)
    const isNetworkDown = !error.response && (error.code === 'ERR_NETWORK' || !navigator.onLine);
    if (isNetworkDown) {
      const { config } = error;
      // Mettiamo in coda solo le modifiche (POST/PUT/DELETE), non le GET
      if (config && config.method && config.method.toLowerCase() !== 'get') {
        const savedId = OfflineStorage.addToQueue(config);
        if (savedId) {
          // Coda OK: avvisa l'utente con un toast globale (gestito da SyncManager)
          window.dispatchEvent(new CustomEvent('offline-queued'));
          return Promise.reject({
            isOffline: true,
            queued: true,
            message: 'Nessuna connessione: i dati sono stati salvati sul dispositivo e verranno inviati appena torni online.'
          });
        }
        // Salvataggio in coda FALLITO (memoria piena / allegato troppo grande): NON illudere l'utente
        window.dispatchEvent(new CustomEvent('offline-queue-failed'));
        return Promise.reject({
          isOffline: true,
          queued: false,
          message: 'Sei offline e non è stato possibile salvare sul dispositivo (memoria piena o allegato troppo grande). Riprova quando torni online.'
        });
      }
    }

    if (error.response?.status === 401) {
      // Le richieste RIMANDATE dalla coda offline non devono buttare fuori l'utente:
      // se ne occupa SyncManager, che le lascia in coda e riprova dopo l'accesso.
      // Senza questa distinzione un 401 durante il replay ricaricava la pagina, che
      // rimontava SyncManager, che risincronizzava, che riprendeva 401... ciclo
      // infinito sulla pagina di login, con i campi che si svuotavano da soli.
      if (error.config?.replayCodaOffline) {
        return Promise.reject(error);
      }

      store.dispatch(logout());

      // Nessuna ricarica forzata se si è GIÀ sul login: il redirect a tappeto era
      // l'innesco del ciclo. Dalle pagine protette ci pensa ProtectedRoute a
      // riportare al login, senza ricaricare tutta l'applicazione.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  switchRole: (role) => api.post('/auth/switch-role', { role }),
  updateProfilo: (data) => api.put('/auth/profilo', data),
};

export const auditService = {
  getAll: (params) => api.get('/audit', { params }),
};

export const offlineDataService = {
  getGiorno: (data) => api.get('/offline/giorno', { params: data ? { data } : {} }),
};

export const clientiService = {
  getAll: () => api.get('/clienti'),
  getById: (id) => api.get(`/clienti/${id}`),
  create: (data) => api.post('/clienti', data),
  update: (id, data) => api.put(`/clienti/${id}`, data),
  delete: (id, params) => api.delete(`/clienti/${id}`, { params }),
};

export const cantieriService = {
  getAll: () => api.get('/cantieri'),
  getById: (id) => api.get(`/cantieri/${id}`),
  create: (data) => api.post('/cantieri', data),
  update: (id, data) => api.put(`/cantieri/${id}`, data),
  delete: (id) => api.delete(`/cantieri/${id}`),
  // Task management
  getTasks: (cantiereId) => api.get(`/cantieri/${cantiereId}/tasks`),
  importTasks: (cantiereId, formData) => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    }).post(`/cantieri/${cantiereId}/import-tasks`, formData);
  },
  updateTask: (taskId, data) => api.put(`/cantieri/tasks/${taskId}`, data),
  deleteTask: (taskId) => api.delete(`/cantieri/tasks/${taskId}`),
  downloadTemplate: () => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      responseType: 'blob', // Importante per il download di file
    }).get('/cantieri/template-tasks/download');
  },
};

export const interventiService = {
  getAll: () => api.get('/interventi'),
  getById: (id) => api.get(`/interventi/${id}`),
  create: (data) => api.post('/interventi', data),
  update: (id, data) => api.put(`/interventi/${id}`, data),
  // Punto 2: il capocantiere aggiorna SOLO i tecnici del proprio intervento (endpoint scoped)
  updateTecnici: (id, data) => api.put(`/interventi/${id}/tecnici`, data),
  // #2: bozza "lavori svolti" salvabile dal capocantiere durante il lavoro
  updateDescrizioneRapportino: (id, data) => api.put(`/interventi/${id}/descrizione-rapportino`, data),
  delete: (id) => api.delete(`/interventi/${id}`),
  deleteRicorrenza: (ricorrenzaId) => api.delete(`/interventi/ricorrenza/${ricorrenzaId}`),
};

export const calendarioService = {
  getEventi: (params) => api.get('/calendario', { params }),
};

export const usersService = {
  getAll: () => api.get('/users'),
  getCapocantieri: () => api.get('/users/capocantieri'),
  getTecnici: () => api.get('/users/tecnici'),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const statsService = {
  getStats: () => api.get('/stats'),
};

export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),
};

export const timbratureService = {
  getByIntervento: (interventoId) => api.get(`/timbrature/intervento/${interventoId}`),
  create: (data) => api.post('/timbrature', data),
  update: (id, data) => api.put(`/timbrature/${id}`, data),
  delete: (id) => api.delete(`/timbrature/${id}`),
  cleanupDuplicates: () => api.post('/timbrature/cleanup-duplicates'),
};

export const presenzeService = {
  getByIntervento: (interventoId) => api.get(`/presenze/intervento/${interventoId}`),
  create: (data) => api.post('/presenze', data),
  update: (id, data) => api.put(`/presenze/${id}`, data),
  delete: (id) => api.delete(`/presenze/${id}`),
  registraUscitaAnticipata: (presenzaId, data) => api.post(`/presenze/${presenzaId}/uscita-anticipata`, data),
  // Punto 5: orari individuali (entrata/uscita) di una persona sull'intervento
  setOrari: (presenzaId, data) => api.post(`/presenze/${presenzaId}/orari`, data),
};

export const rapportiniService = {
  getAll: (params) => api.get('/rapportini', { params }),
  getUtenti: () => api.get('/rapportini/utenti'),
  getDaCompilare: (params) => api.get('/rapportini/da-compilare', { params }),
  create: (data) => api.post('/rapportini', data),
  getCalcolo: (interventoId) => api.get(`/rapportini/calcolo/${interventoId}`),
  getByIntervento: (interventoId) => api.get(`/rapportini/intervento/${interventoId}`),
  getByCliente: (clienteId) => api.get(`/rapportini/cliente/${clienteId}`),
  getByCantiere: (cantiereId, params) => api.get(`/rapportini/cantiere/${cantiereId}`, { params }),
  getById: (id) => api.get(`/rapportini/${id}`),
  update: (id, data) => api.put(`/rapportini/${id}`, data),
};

export const veicoliService = {
  getAll: () => api.get('/veicoli'),
  getAttivi: () => api.get('/veicoli/attivi'),
  getDisponibili: (data, interventoId) => {
    const params = { data };
    if (interventoId) params.intervento_id = interventoId;
    return api.get('/veicoli/disponibili', { params });
  },
  getById: (id) => api.get(`/veicoli/${id}`),
  create: (data) => api.post('/veicoli', data),
  update: (id, data) => api.put(`/veicoli/${id}`, data),
  delete: (id) => api.delete(`/veicoli/${id}`),
};

export default api;